import { type Graph, nodeById, withoutEdges } from '../models/graph';
import { shortestPathLength } from './shortestPath';

export function shortestPathInterdiction(
  graph: Graph,
  sourceId: string,
  targetId: string,
  budget: number,
): string[] {
  if (budget <= 0) return [];
  if (!nodeById(graph, sourceId) || !nodeById(graph, targetId)) return [];
  if (graph.edges.length === 0) return [];

  if (budget <= 3 && graph.edges.length <= 30) {
    return bruteForce(graph, sourceId, targetId, budget);
  }
  return greedy(graph, sourceId, targetId, budget);
}

function bruteForce(graph: Graph, sourceId: string, targetId: string, budget: number): string[] {
  let bestLength = shortestPathLength(graph, sourceId, targetId);
  let bestRemoval: string[] = [];

  const edgeIds = graph.edges.map((e) => e.id);
  for (const combo of combinations(edgeIds, budget)) {
    const reduced = withoutEdges(graph, new Set(combo));
    const length = shortestPathLength(reduced, sourceId, targetId);
    if (length > bestLength) {
      bestLength = length;
      bestRemoval = combo;
      if (bestLength === Infinity) break;
    }
  }

  return bestRemoval;
}

function greedy(graph: Graph, sourceId: string, targetId: string, budget: number): string[] {
  const removed: string[] = [];
  let currentGraph = graph;

  for (let i = 0; i < budget; i++) {
    const currentLength = shortestPathLength(currentGraph, sourceId, targetId);
    if (currentLength === Infinity) break;

    let bestEdge: string | null = null;
    let bestLength = currentLength;

    for (const edge of currentGraph.edges) {
      const reduced = withoutEdges(currentGraph, new Set([edge.id]));
      const length = shortestPathLength(reduced, sourceId, targetId);
      if (length > bestLength) {
        bestLength = length;
        bestEdge = edge.id;
        if (bestLength === Infinity) break;
      }
    }

    if (!bestEdge) break;
    removed.push(bestEdge);
    currentGraph = withoutEdges(currentGraph, new Set([bestEdge]));
  }

  return removed;
}

function combinations(elements: readonly string[], maxSize: number): string[][] {
  const result: string[][] = [];

  function generate(start: number, current: string[]): void {
    if (current.length > 0) result.push([...current]);
    if (current.length === maxSize) return;

    for (let i = start; i < elements.length; i++) {
      current.push(elements[i]!);
      generate(i + 1, current);
      current.pop();
    }
  }

  generate(0, []);
  return result;
}
